import React, { useMemo } from "react"
import { useRecoilValue } from "recoil"

import {
  buyableProductsFor,
  purchasableUnitPrice,
} from "../../helpers/buyableProducts"
import { PlanItemProductName, planItemFor } from "../../helpers/productKeys"
import { usePurchaseModal } from "../../hooks/usePurchaseModal"
import { PositiveNumber } from "../../interfaces"
import { t } from "../../lib/i18n"
import logger from "../../lib/logger"
import useCurrentService from "../../recoils/atoms/currentService"
import useCurrentTeam from "../../recoils/atoms/currentTeam"
import usePlans from "../../recoils/atoms/plans"

import Title from "./Title"
import {
  Cell,
  CellContainer,
  CellDivider,
  CellHeader,
  CellList,
  CellPrice,
  CellTitle,
  CellUnit,
  SelectButton,
} from "./cell/Cell"

type SinglePurchase = {
  productName: PlanItemProductName
  price: PositiveNumber
}

// 単体購入の節。
//
// **描くセルも、押したときに開くモーダルも、helpers/buyableProducts.ts の
// 1 つの表から導きます。** 以前はサービス名でハードコードした分岐がセルを描き、
// 「選択」が `setPurchaseConfirmModal` を直接呼んでいたため、
// `usePurchaseModal` 側のガード（`buyableItems`）を通らない 2 本目の経路が
// できていました。CLOUD で購入確認が開かなかったのは TODO ガード 1 枚のおかげで、
// `buyableItems.cloud` を空にした #414 はこちらの経路に効いていません（#415）。
//
// 案C では CLOUD に買い足せる商品が 1 つも無いので、CLOUD ではこの節ごと出ません。
// 「CLOUD だから出さない」ではなく「**買える商品が 0 件だから出さない**」です。
const SinglePurchases = (): JSX.Element => {
  const currentService = useRecoilValue(useCurrentService)
  const currentTeam = useRecoilValue(useCurrentTeam)
  const plans = useRecoilValue(usePlans)
  const { openSinglePurchaseModal } = usePurchaseModal()

  const purchases: SinglePurchase[] = useMemo(() => {
    const plan = plans.list.find(
      (p) => p.planType === currentTeam.planTypes[currentService.name],
    )

    if (!plan) return []

    // 単価が引けない商品はセルごと出さない。**2 通りの引けなさを両方落とします**:
    //   - `planItemFor` が undefined … そのサービスに無い商品。`¥undefined` になる
    //   - `price` が正の数でない    … `findProductItem` が product / price 不在で
    //     `price: 0` に倒したもの。`¥0` のセルとボタンになり、押すと
    //     `PurchaseConfirmModal` が `!price` で自分を閉じて
    //     **理由の出ない notification.error** を出す
    //
    // `price: 0` を落として構わない理由（= 正当な無料商品と取り違えない理由）は
    // `purchasableUnitPrice` のコメントを参照。
    return buyableProductsFor(currentService.name).flatMap((productName) => {
      const item = planItemFor(plan.items, productName)

      const price = purchasableUnitPrice(item)

      if (price === undefined) {
        // 黙って落とさない。買える商品として表に載っているのに単価が引けないのは
        // auth-api 側の欠落か、表に無料商品を足してしまったかのどちらかで、
        // どちらも defect（base の 553b305 と同じ方針）。
        logger(
          "SinglePurchases: 単価が引けないのでセルを描きませんでした",
          {
            productName: productName,
            serviceName: currentService.name,
            planType: plan.planType,
            price: item?.price,
          },
          new Error("missing unit price"),
        )

        return []
      }

      return [{ productName, price: price }]
    })
  }, [currentService, currentTeam, plans])

  if (!purchases.length) return <></>

  return (
    <div>
      <Title title={t("single_purchase")} />
      <CellList>
        {purchases.map((purchase, index) => (
          <React.Fragment key={purchase.productName}>
            {index > 0 && <CellDivider />}
            <Cell>
              <CellContainer>
                <CellHeader>
                  <CellTitle>{t(`add_${purchase.productName}`)}</CellTitle>
                  <CellPrice>¥{purchase.price.toLocaleString()}</CellPrice>
                </CellHeader>
                <CellUnit>{t(`${purchase.productName}_per_unit`)}</CellUnit>
                <SelectButton
                  type="button"
                  onClick={() => openSinglePurchaseModal(purchase.productName)}
                >
                  {t("select")}
                </SelectButton>
              </CellContainer>
            </Cell>
          </React.Fragment>
        ))}
      </CellList>
    </div>
  )
}

export default SinglePurchases
